/**
 * 逐 case Δ 召回率列表（mock v3 「Case 级 Δ 召回率」下钻）。
 *
 * 后端已按 Δ 升序排好（恶化在前、不可比置底），这里保留该默认序：先看退化项是这张表
 * 的用途。``deltaPp`` 为 null 表示某一侧算不出召回率（GT 为 0 等），不参与分档也不染色。
 */
import { Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type { PerceptionCaseDelta } from '@/apis/simulation';
import ApiCodeLink from '@components/ApiCodeLink';
import { sceneGroupDisplay } from '@/constants/sceneGroup';

import { deltaColor, EMPTY, fmtPp, fmtRatio } from '../format';
import { BUCKET_DEFS } from './DeltaBucketCards';

/** 标签为 NULL/空时后端给的哨兵，与 job_service.query_scene_labels_by_job 一致。 */
const UNLABELED = '__unlabeled__';

const bucketLabel = (key: string | null): string =>
  BUCKET_DEFS.find((d) => d.key === key)?.label ?? EMPTY;

const bucketColor = (key: string | null): string | undefined => {
  switch (key) {
    case 'improved':
      return 'green';
    case 'slightlyImproved':
      return 'lime';
    case 'slightlyDegraded':
      return 'orange';
    case 'degraded':
      return 'red';
    default:
      return undefined;
  }
};

const columns: ColumnsType<PerceptionCaseDelta> = [
  {
    title: 'Case',
    dataIndex: 'caseId',
    key: 'caseId',
    width: 160,
    render: (v: string | null) => v || EMPTY,
  },
  {
    title: 'API_CODE',
    dataIndex: 'apiCode',
    key: 'apiCode',
    width: 140,
    render: (v: string | null, r) => <ApiCodeLink code={v} source={r.source} />,
  },
  {
    title: '场景分组',
    dataIndex: 'sceneGroup',
    key: 'sceneGroup',
    width: 120,
    render: (v: string | null) => sceneGroupDisplay(v),
  },
  {
    title: (
      <Tooltip title="scene_inspection.label 原文（逗号拼接的多值串）；分组统计用的是 scene_group">
        场景标签
      </Tooltip>
    ),
    dataIndex: 'label',
    key: 'label',
    width: 180,
    ellipsis: true,
    render: (v: string | null) => (!v || v === UNLABELED ? EMPTY : v),
  },
  {
    title: '基线召回率',
    dataIndex: 'baseRecall',
    key: 'baseRecall',
    width: 100,
    align: 'right',
    render: (v) => fmtRatio(v),
  },
  {
    title: '本次召回率',
    dataIndex: 'currentRecall',
    key: 'currentRecall',
    width: 100,
    align: 'right',
    render: (v) => fmtRatio(v),
  },
  {
    title: 'Δ 召回率',
    dataIndex: 'deltaPp',
    key: 'deltaPp',
    width: 110,
    align: 'right',
    sorter: (a, b) => (a.deltaPp ?? 0) - (b.deltaPp ?? 0),
    render: (v: number | null) => <span style={{ color: deltaColor(v) }}>{fmtPp(v)}</span>,
  },
  {
    title: '档位',
    dataIndex: 'bucket',
    key: 'bucket',
    width: 100,
    render: (v: string | null) =>
      v ? <Tag color={bucketColor(v)}>{bucketLabel(v)}</Tag> : EMPTY,
  },
];

export default function CaseDeltaTable({ rows }: { rows: PerceptionCaseDelta[] }) {
  return (
    <Table<PerceptionCaseDelta>
      rowKey="sceneId"
      size="small"
      dataSource={rows}
      columns={columns}
      tableLayout="fixed"
      scroll={{ x: 1010 }}
      pagination={{
        defaultPageSize: 20,
        showSizeChanger: true,
        pageSizeOptions: ['20', '50', '100', '500'],
        showQuickJumper: true,
        showTotal: (t) => `总共 ${t} 个 case`,
      }}
    />
  );
}
